import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, ChevronLeft, ChevronRight, Pencil, RefreshCw, Search, X } from "lucide-react";
import { toast } from "sonner";
import {
  getGameBundle,
  getMapRespawnStats,
  listAutoMapHuntLeases,
  releaseAutoMapHuntLease,
  searchGameServers,
  updateMapRespawnTarget,
  type GameMap,
} from "@/api/games";
import type {
  AutoMapHuntLeaseEntry,
  GameServerEntry,
  MapRespawnStatEntry,
  MapRespawnTargetEntry,
  MapRespawnWindowEntry,
} from "@/api/types";
import { Button } from "@/components/ui/Button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/Card";
import { Checkbox } from "@/components/ui/Checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/Dialog";
import { Field, Input } from "@/components/ui/Input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select";
import { StatusBadge } from "@/components/ui/StatusBadge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/Tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { useSSESubscription, type SSEMessage } from "@/hooks/useSSESubscription";

type MapDirectoryEntry = Pick<GameMap, "map_id" | "name">;
type RuntimeView = "overview" | "targets" | "stats" | "leases";
const TARGET_PAGE_SIZE_OPTIONS = [20, 50, 100] as const;

export function AutoMapHuntRuntimePanel({
  gameID,
  maps,
}: {
  gameID: string;
  maps?: MapDirectoryEntry[];
}) {
  const queryClient = useQueryClient();
  const [serverSearch, setServerSearch] = useState("");
  const [serverPickerOpen, setServerPickerOpen] = useState(false);
  const [view, setView] = useState<RuntimeView>("overview");
  const [lastRealtimeAt, setLastRealtimeAt] = useState(0);
  const serversQ = useQuery({
    queryKey: ["game-servers", gameID, serverSearch],
    queryFn: () => searchGameServers(gameID, serverSearch, 10),
    staleTime: 15_000,
  });
  const servers = serversQ.data?.items ?? [];
  const defaultServerID = servers[0]?.server_id ?? "";
  const [selectedServerID, setSelectedServerID] = useState(defaultServerID);

  useEffect(() => {
    if (selectedServerID.trim() === "" && defaultServerID !== "") {
      setSelectedServerID(defaultServerID);
    }
  }, [defaultServerID, selectedServerID]);

  const mapsQ = useQuery({
    queryKey: ["game-bundle", gameID],
    queryFn: () => getGameBundle(gameID),
    enabled: !maps,
    staleTime: 30_000,
  });
  const mapNameByID = useMemo(() => {
    const lookup = new Map<string, string>();
    for (const map of maps ?? mapsQ.data?.bundle.maps ?? []) {
      const mapID = map.map_id.trim();
      const name = map.name.trim();
      if (mapID && name) lookup.set(mapID, name);
    }
    return lookup;
  }, [maps, mapsQ.data]);

  const effectiveServerID = selectedServerID.trim();
  const respawnQ = useQuery({
    queryKey: ["map-respawn-stats", gameID, effectiveServerID],
    queryFn: () => getMapRespawnStats(gameID, effectiveServerID),
    enabled: effectiveServerID !== "",
  });
  const leasesQ = useQuery({
    queryKey: ["auto-map-hunt-leases", gameID, effectiveServerID],
    queryFn: () => listAutoMapHuntLeases(gameID, effectiveServerID),
    enabled: effectiveServerID !== "",
  });
  const respawnRows = useMemo(() => respawnQ.data?.items ?? [], [respawnQ.data]);
  const respawnTargets = useMemo(
    () => respawnQ.data?.targets ?? [],
    [respawnQ.data],
  );
  const leaseRows = leasesQ.data?.items ?? [];
  const canonicalServerID =
    respawnQ.data?.canonical_server_id ||
    leasesQ.data?.canonical_server_id ||
    servers.find((server) => server.server_id === effectiveServerID)
      ?.effective_main_server_id ||
    effectiveServerID;

  const scheduleRuntimeRefresh = useRuntimeRealtimeRefresh({
    gameID,
    serverID: effectiveServerID,
    canonicalServerID,
    queryClient,
    onRealtime: setLastRealtimeAt,
  });
  useSSESubscription("account_map_stats_updated", scheduleRuntimeRefresh, {
    enabled: effectiveServerID !== "",
  });
  useSSESubscription("map_respawn_target_updated", scheduleRuntimeRefresh, {
    enabled: effectiveServerID !== "",
  });
  useSSESubscription("auto_map_hunt_lease_updated", scheduleRuntimeRefresh, {
    enabled: effectiveServerID !== "",
  });

  function refreshAll() {
    void respawnQ.refetch();
    void leasesQ.refetch();
    void serversQ.refetch();
  }

  const selectedServer = servers.find(
    (server) => server.server_id === effectiveServerID,
  );
  const selectedServerLabel =
    selectedServer?.display_name ||
    selectedServer?.server_name ||
    selectedServer?.server_id ||
    effectiveServerID ||
    "未选择";

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="space-y-3 p-3">
          <div className="grid gap-3 xl:grid-cols-[minmax(220px,1fr)_minmax(320px,1.5fr)_auto] xl:items-end">
            <div className="grid grid-cols-4 gap-2">
              <MetricTile label="目标" value={respawnTargets.length} />
              <MetricTile label="统计" value={respawnRows.length} />
              <MetricTile label="占用" value={leaseRows.length} />
              <MetricTile label="归一区" value={canonicalServerID || "-"} mono />
            </div>
            <div className="grid gap-2 lg:grid-cols-[minmax(180px,1fr)_220px]">
              <Field label="搜索区服">
                <div className="relative">
                  <Search
                    className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-[hsl(var(--muted-foreground))]"
                    aria-hidden="true"
                  />
                  <Input
                    value={serverSearch}
                    onChange={(e) => {
                      setServerSearch(e.target.value);
                      setServerPickerOpen(true);
                    }}
                    onFocus={() => setServerPickerOpen(true)}
                    placeholder="区服名 / server_id"
                    className="pl-8"
                  />
                </div>
              </Field>
              <Field label="运行观测区服 ID">
                <Input
                  value={selectedServerID}
                  onChange={(e) => setSelectedServerID(e.target.value)}
                  placeholder="24"
                />
              </Field>
            </div>
            <div className="flex flex-wrap items-center justify-end gap-2">
              <StatusBadge variant={lastRealtimeAt ? "ok" : "neutral"}>
                {lastRealtimeAt ? `实时 ${formatTime(lastRealtimeAt)}` : "等待实时"}
              </StatusBadge>
              <Button
                size="sm"
                variant="outline"
                className="gap-1"
                disabled={!effectiveServerID || respawnQ.isFetching || leasesQ.isFetching}
                onClick={refreshAll}
              >
                <RefreshCw className="size-3.5" aria-hidden="true" />
                刷新
              </Button>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-xs text-[hsl(var(--muted-foreground))]">
            <span>当前区服：{selectedServerLabel}</span>
            {respawnQ.data || leasesQ.data ? (
              <span>
                查询区服：
                {respawnQ.data?.server_id || leasesQ.data?.server_id || effectiveServerID || "-"}
              </span>
            ) : null}
            {serversQ.isFetching ? <span>搜索中...</span> : null}
            {serversQ.isError ? (
              <span className="text-[hsl(var(--danger))]">
                {(serversQ.error as Error).message}
              </span>
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-6 px-2"
              onClick={() => setServerPickerOpen((open) => !open)}
            >
              {serverPickerOpen ? "收起区服" : "展开区服"}
            </Button>
          </div>
          {serverPickerOpen ? (
            <RuntimeServerTable
              items={servers}
              loading={serversQ.isFetching}
              selectedServerID={selectedServerID}
              onSelect={(serverID) => {
                setSelectedServerID(serverID);
                setServerPickerOpen(false);
              }}
            />
          ) : null}
        </CardContent>
      </Card>

      {!effectiveServerID ? (
        <Card>
          <CardContent>
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              选择或输入观测区服 ID 后可查看实时刷怪聚合和自动下图占用。
            </p>
          </CardContent>
        </Card>
      ) : (
        <Tabs value={view} onValueChange={(next) => setView(next as RuntimeView)}>
          <TabsList>
            <TabsTrigger value="overview">概览</TabsTrigger>
            <TabsTrigger value="targets">刷怪目标</TabsTrigger>
            <TabsTrigger value="stats">刷怪统计</TabsTrigger>
            <TabsTrigger value="leases">占用</TabsTrigger>
          </TabsList>
          <TabsContent value="overview">
            <AutoMapHuntOverview
              targets={respawnTargets}
              stats={respawnRows}
              leases={leaseRows}
              loading={respawnQ.isFetching || leasesQ.isFetching}
              error={runtimeError(respawnQ.error, leasesQ.error)}
              mapNameByID={mapNameByID}
              onSelectView={setView}
            />
          </TabsContent>
          <TabsContent value="targets">
            <RespawnTargetsPanel
              gameID={gameID}
              serverID={effectiveServerID}
              targets={respawnTargets}
              loading={respawnQ.isFetching}
              error={respawnQ.error}
              mapNameByID={mapNameByID}
              onRefresh={() => void respawnQ.refetch()}
            />
          </TabsContent>
          <TabsContent value="stats">
            <RespawnStatsPanel
              items={respawnRows}
              loading={respawnQ.isFetching}
              error={respawnQ.error}
              mapNameByID={mapNameByID}
              onRefresh={() => void respawnQ.refetch()}
            />
          </TabsContent>
          <TabsContent value="leases">
            <AutoMapHuntLeasesPanel
              gameID={gameID}
              items={leaseRows}
              loading={leasesQ.isFetching}
              error={leasesQ.error}
              mapNameByID={mapNameByID}
              onRefresh={() => void leasesQ.refetch()}
            />
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
}

function RuntimeServerTable({
  items,
  loading,
  selectedServerID,
  onSelect,
}: {
  items: GameServerEntry[];
  loading: boolean;
  selectedServerID: string;
  onSelect: (serverID: string) => void;
}) {
  if (items.length === 0) {
    return (
      <p className="text-xs text-[hsl(var(--muted-foreground))]">
        {loading ? "正在读取区服目录..." : "目录中暂无匹配区服。Lua 上报新 server_id 后会自动补入目录。"}
      </p>
    );
  }
  return (
    <Table dense>
      <TableHeader>
        <TableRow>
          <TableHead>区服</TableHead>
          <TableHead>别名</TableHead>
          <TableHead>主区</TableHead>
          <TableHead>角色</TableHead>
          <TableHead>最近出现</TableHead>
          <TableHead>操作</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((server) => (
          <TableRow key={server.server_id}>
            <TableCell>
              <div className="font-medium">
                {server.display_name || server.server_name || server.server_id}
              </div>
              <div className="font-mono text-xs text-[hsl(var(--muted-foreground))]">
                {server.server_id}
                {server.display_name && server.server_name ? ` · ${server.server_name}` : ""}
              </div>
            </TableCell>
            <TableCell>{server.alias || "-"}</TableCell>
            <TableCell className="font-mono">{server.effective_main_server_id || server.server_id || "-"}</TableCell>
            <TableCell>{server.role_count}</TableCell>
            <TableCell>{formatTime(server.last_seen_at ?? 0)}</TableCell>
            <TableCell>
              <Button
                type="button"
                size="sm"
                variant={selectedServerID === server.server_id ? "default" : "outline"}
                className="gap-1"
                onClick={() => onSelect(server.server_id)}
              >
                <Check className="size-3.5" aria-hidden="true" />
                {selectedServerID === server.server_id ? "已选" : "选择"}
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function useRuntimeRealtimeRefresh({
  gameID,
  serverID,
  canonicalServerID,
  queryClient,
  onRealtime,
}: {
  gameID: string;
  serverID: string;
  canonicalServerID?: string;
  queryClient: ReturnType<typeof useQueryClient>;
  onRealtime: (time: number) => void;
}) {
  const timerRef = useRef<number | null>(null);

  useEffect(
    () => () => {
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
    },
    [],
  );

  return useCallback(
    (payload: SSEMessage) => {
      if (!runtimeEventMatches(payload, gameID, serverID, canonicalServerID)) {
        return;
      }
      onRealtime(Date.now());
      if (timerRef.current !== null) {
        window.clearTimeout(timerRef.current);
      }
      timerRef.current = window.setTimeout(() => {
        void queryClient.invalidateQueries({
          queryKey: ["map-respawn-stats", gameID, serverID],
        });
        void queryClient.invalidateQueries({
          queryKey: ["auto-map-hunt-leases", gameID, serverID],
        });
      }, 300);
    },
    [canonicalServerID, gameID, onRealtime, queryClient, serverID],
  );
}

function runtimeEventMatches(
  payload: SSEMessage,
  gameID: string,
  serverID: string,
  canonicalServerID?: string,
) {
  const payloadGameID = typeof payload.game_id === "string" ? payload.game_id : "";
  if (payloadGameID && payloadGameID !== gameID) return false;
  const payloadServerID =
    typeof payload.server_id === "string" ? payload.server_id : "";
  const payloadCanonical =
    typeof payload.canonical_server_id === "string"
      ? payload.canonical_server_id
      : "";
  if (!payloadServerID && !payloadCanonical) return true;
  if (payloadServerID && payloadServerID === serverID) return true;
  return Boolean(
    payloadCanonical && canonicalServerID && payloadCanonical === canonicalServerID,
  );
}

function MetricTile({
  label,
  value,
  mono,
}: {
  label: string;
  value: number | string;
  mono?: boolean;
}) {
  return (
    <div className="rounded border border-[hsl(var(--border))] px-3 py-2">
      <div className="text-xs text-[hsl(var(--muted-foreground))]">{label}</div>
      <div
        className={
          mono ? "truncate font-mono text-sm" : "text-lg font-semibold leading-tight"
        }
      >
        {value}
      </div>
    </div>
  );
}

function AutoMapHuntOverview({
  targets,
  stats,
  leases,
  loading,
  error,
  mapNameByID,
  onSelectView,
}: {
  targets: MapRespawnTargetEntry[];
  stats: MapRespawnStatEntry[];
  leases: AutoMapHuntLeaseEntry[];
  loading: boolean;
  error: Error | null;
  mapNameByID: Map<string, string>;
  onSelectView: (view: RuntimeView) => void;
}) {
  const bossTargets = targets.filter((target) => target.kind === "boss");
  const weakTargets = targets.filter(
    (target) => !target.disabled && target.confidence < 0.7,
  );
  const disabledTargets = targets.filter((target) => target.disabled);
  const lowSampleStats = stats.filter((item) => item.sample_count < 3);
  const recentTargets = [...targets]
    .sort((a, b) => (b.updated_at ?? 0) - (a.updated_at ?? 0))
    .slice(0, 8);

  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,1fr)_320px]">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between gap-3">
            <CardTitle>实时概览</CardTitle>
            {loading ? <StatusBadge variant="neutral">同步中</StatusBadge> : null}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? (
            <p className="text-xs text-[hsl(var(--danger))]">{error.message}</p>
          ) : null}
          <div className="grid gap-2 lg:grid-cols-4">
            <MetricTile label="Boss 目标" value={bossTargets.length} />
            <MetricTile label="低置信目标" value={weakTargets.length} />
            <MetricTile label="样本少" value={lowSampleStats.length} />
            <MetricTile label="禁用目标" value={disabledTargets.length} />
          </div>
          {recentTargets.length > 0 ? (
            <Table dense>
              <TableHeader>
                <TableRow>
                  <TableHead>最近目标</TableHead>
                  <TableHead>地图</TableHead>
                  <TableHead>窗口</TableHead>
                  <TableHead>状态</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {recentTargets.map((target) => (
                  <TableRow
                    key={`${target.canonical_server_id}:${target.map_id}:${target.target_key}`}
                  >
                    <TableCell>{target.display_name || formatTargetName(target)}</TableCell>
                    <TableCell>
                      <MapNameCell
                        mapID={target.map_id}
                        mapNameByID={mapNameByID}
                        compact
                      />
                    </TableCell>
                    <TableCell>{formatWindows(target.windows ?? [])}</TableCell>
                    <TableCell>
                      <StatusBadge
                        variant={
                          target.disabled
                            ? "warn"
                            : target.confidence >= 0.7
                              ? "ok"
                              : "warn"
                        }
                      >
                        {target.disabled ? "禁用" : `${Math.round(target.confidence * 100)}%`}
                      </StatusBadge>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-xs text-[hsl(var(--muted-foreground))]">
              暂无刷怪目标，等待 Lua 上报样本后自动出现。
            </p>
          )}
        </CardContent>
      </Card>
      <div className="space-y-3">
        <OverviewAction
          title="刷怪目标"
          description={`${targets.length} 个目标，可确认 Boss、校准模型或禁用误识别`}
          onClick={() => onSelectView("targets")}
        />
        <OverviewAction
          title="刷怪统计"
          description={`${stats.length} 条 EWMA 统计，${lowSampleStats.length} 条样本不足`}
          onClick={() => onSelectView("stats")}
        />
        <OverviewAction
          title="自动下图占用"
          description={`${leases.length} 个 lease，必要时可手动释放`}
          onClick={() => onSelectView("leases")}
        />
      </div>
    </div>
  );
}

function OverviewAction({
  title,
  description,
  onClick,
}: {
  title: string;
  description: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className="w-full rounded border border-[hsl(var(--border))] bg-[hsl(var(--card))] p-3 text-left transition-colors hover:bg-[hsl(var(--muted))]"
      onClick={onClick}
    >
      <div className="text-sm font-medium">{title}</div>
      <div className="mt-1 text-xs text-[hsl(var(--muted-foreground))]">
        {description}
      </div>
    </button>
  );
}

function RespawnTargetsPanel({
  gameID,
  serverID,
  targets,
  loading,
  error,
  mapNameByID,
  onRefresh,
}: {
  gameID: string;
  serverID: string;
  targets: MapRespawnTargetEntry[];
  loading: boolean;
  error: unknown;
  mapNameByID: Map<string, string>;
  onRefresh: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle>刷怪目标</CardTitle>
          <Button
            size="sm"
            variant="outline"
            className="gap-1"
            disabled={loading}
            onClick={onRefresh}
          >
            <RefreshCw className="size-3.5" aria-hidden="true" />
            刷新
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error ? (
          <p className="text-xs text-[hsl(var(--danger))]">{(error as Error).message}</p>
        ) : targets.length === 0 ? (
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            暂无刷怪目标。
          </p>
        ) : (
          <RespawnTargetsTable
            gameID={gameID}
            serverID={serverID}
            targets={targets}
            mapNameByID={mapNameByID}
          />
        )}
      </CardContent>
    </Card>
  );
}

function RespawnStatsPanel({
  items,
  loading,
  error,
  mapNameByID,
  onRefresh,
}: {
  items: MapRespawnStatEntry[];
  loading: boolean;
  error: unknown;
  mapNameByID: Map<string, string>;
  onRefresh: () => void;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle>刷怪统计</CardTitle>
          <Button
            size="sm"
            variant="outline"
            className="gap-1"
            disabled={loading}
            onClick={onRefresh}
          >
            <RefreshCw className="size-3.5" aria-hidden="true" />
            刷新
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error ? (
          <p className="text-xs text-[hsl(var(--danger))]">{(error as Error).message}</p>
        ) : items.length === 0 ? (
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            暂无刷怪样本。
          </p>
        ) : (
          <RespawnStatsTable items={items} mapNameByID={mapNameByID} />
        )}
      </CardContent>
    </Card>
  );
}

function RespawnTargetsTable({
  gameID,
  serverID,
  targets,
  mapNameByID,
}: {
  gameID: string;
  serverID: string;
  targets: MapRespawnTargetEntry[];
  mapNameByID: Map<string, string>;
}) {
  const [editing, setEditing] = useState<MapRespawnTargetEntry | null>(null);
  const [search, setSearch] = useState("");
  const [kindFilter, setKindFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [pageSize, setPageSize] =
    useState<(typeof TARGET_PAGE_SIZE_OPTIONS)[number]>(20);
  const [page, setPage] = useState(1);
  const queryClient = useQueryClient();
  const confirmBossMut = useMutation({
    mutationFn: (target: MapRespawnTargetEntry) =>
      updateMapRespawnTarget(gameID, {
        server_id: serverID,
        canonical_server_id: target.canonical_server_id,
        map_id: target.map_id,
        target_key: target.target_key,
        display_name: target.display_name || formatTargetName(target),
        kind: "boss",
        model: "death_cooldown",
        disabled: target.disabled,
        priority: target.priority,
        merged_into_target_key: target.merged_into_target_key || "",
      }),
    onSuccess: () => {
      toast.success("Boss 目标已确认");
      void queryClient.invalidateQueries({ queryKey: ["map-respawn-stats", gameID] });
    },
    onError: (e) => toast.error(`确认失败：${(e as Error).message}`),
  });

  function confirmBoss(target: MapRespawnTargetEntry) {
    const name = target.display_name || formatTargetName(target);
    if (!window.confirm(`确认将 ${name} 标记为 Boss 死亡冷却目标？`)) {
      return;
    }
    confirmBossMut.mutate(target);
  }

  const filteredTargets = useMemo(() => {
    const keyword = search.trim().toLowerCase();
    return targets.filter((target) => {
      if (kindFilter !== "all" && target.kind !== kindFilter) return false;
      if (statusFilter === "active" && target.disabled) return false;
      if (statusFilter === "disabled" && !target.disabled) return false;
      if (statusFilter === "weak" && (target.disabled || target.confidence >= 0.7)) {
        return false;
      }
      if (statusFilter === "merged" && !target.merged_into_target_key) return false;
      if (!keyword) return true;
      const haystack = [
        target.map_id,
        mapNameByID.get(target.map_id) ?? "",
        target.target_key,
        target.display_name,
        formatTargetName(target),
        target.model,
        target.aliases?.join(" ") ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(keyword);
    });
  }, [kindFilter, mapNameByID, search, statusFilter, targets]);

  const totalPages = Math.max(1, Math.ceil(filteredTargets.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const pageItems = filteredTargets.slice(
    (safePage - 1) * pageSize,
    safePage * pageSize,
  );

  useEffect(() => {
    setPage(1);
  }, [kindFilter, pageSize, search, statusFilter]);

  return (
    <>
      <div className="grid gap-2 xl:grid-cols-[minmax(240px,1fr)_160px_180px_120px_auto] xl:items-end">
        <Field label="搜索目标">
          <div className="relative">
            <Search
              className="pointer-events-none absolute left-2.5 top-2.5 size-4 text-[hsl(var(--muted-foreground))]"
              aria-hidden="true"
            />
            <Input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="地图 / 目标 / target_key"
              className="pl-8"
            />
          </div>
        </Field>
        <Field label="类型">
          <Select value={kindFilter} onValueChange={setKindFilter}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部</SelectItem>
              <SelectItem value="normal">普通</SelectItem>
              <SelectItem value="elite">精英</SelectItem>
              <SelectItem value="boss">Boss</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="状态">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">全部</SelectItem>
              <SelectItem value="active">启用</SelectItem>
              <SelectItem value="weak">低置信</SelectItem>
              <SelectItem value="merged">已合并</SelectItem>
              <SelectItem value="disabled">禁用</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="每页">
          <Select
            value={String(pageSize)}
            onValueChange={(value) =>
              setPageSize(Number(value) as (typeof TARGET_PAGE_SIZE_OPTIONS)[number])
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TARGET_PAGE_SIZE_OPTIONS.map((option) => (
                <SelectItem key={option} value={String(option)}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>
        <div className="flex items-center justify-end gap-2 text-xs text-[hsl(var(--muted-foreground))]">
          <span>
            {filteredTargets.length === 0
              ? "0 条"
              : `${(safePage - 1) * pageSize + 1}-${Math.min(
                  safePage * pageSize,
                  filteredTargets.length,
                )} / ${filteredTargets.length} 条`}
          </span>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 px-2"
            disabled={safePage <= 1}
            onClick={() => setPage((current) => Math.max(1, current - 1))}
            aria-label="上一页"
          >
            <ChevronLeft className="size-3.5" aria-hidden="true" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="h-8 px-2"
            disabled={safePage >= totalPages}
            onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
            aria-label="下一页"
          >
            <ChevronRight className="size-3.5" aria-hidden="true" />
          </Button>
        </div>
      </div>
      {filteredTargets.length === 0 ? (
        <p className="text-xs text-[hsl(var(--muted-foreground))]">
          当前筛选没有匹配目标。
        </p>
      ) : null}
      <Table dense>
        <TableHeader>
          <TableRow>
            <TableHead>地图</TableHead>
            <TableHead>自动目标</TableHead>
            <TableHead>模型</TableHead>
            <TableHead>多峰窗口</TableHead>
            <TableHead>证据</TableHead>
            <TableHead>状态</TableHead>
            <TableHead>操作</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {pageItems.map((target) => (
            <TableRow key={`${target.canonical_server_id}:${target.map_id}:${target.target_key}`}>
              <TableCell>
                <MapNameCell mapID={target.map_id} mapNameByID={mapNameByID} />
              </TableCell>
              <TableCell>
                <div className="font-medium">{target.display_name || formatTargetName(target)}</div>
                <div className="font-mono text-xs text-[hsl(var(--muted-foreground))]">
                  {target.target_key}
                </div>
              </TableCell>
              <TableCell>
                <div>{formatTargetKind(target.kind)}</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">{target.model}</div>
              </TableCell>
              <TableCell>{formatWindows(target.windows ?? [])}</TableCell>
              <TableCell>
                <div>{target.event_count} 事件 / {target.sample_count} 样本</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">
                  {formatEvidence(target)}
                </div>
              </TableCell>
              <TableCell>
                <StatusBadge variant={target.disabled ? "warn" : target.confidence >= 0.7 ? "ok" : "warn"}>
                  {target.disabled
                    ? "禁用"
                    : target.merged_into_target_key
                      ? "已合并"
                      : `${Math.round(target.confidence * 100)}%`}
                </StatusBadge>
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-2">
                  {target.kind !== "boss" || target.model !== "death_cooldown" ? (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="gap-1"
                      disabled={confirmBossMut.isPending}
                      onClick={() => confirmBoss(target)}
                    >
                      <Check className="size-3.5" aria-hidden="true" />
                      确认Boss
                    </Button>
                  ) : null}
                  <Button type="button" size="sm" variant="outline" className="gap-1" onClick={() => setEditing(target)}>
                    <Pencil className="size-3.5" aria-hidden="true" />
                    校准
                  </Button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      <RespawnTargetDialog
        gameID={gameID}
        serverID={serverID}
        target={editing}
        open={editing !== null}
        onOpenChange={(open) => {
          if (!open) setEditing(null);
        }}
      />
    </>
  );
}

function RespawnTargetDialog({
  gameID,
  serverID,
  target,
  open,
  onOpenChange,
}: {
  gameID: string;
  serverID: string;
  target: MapRespawnTargetEntry | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const queryClient = useQueryClient();
  const [displayName, setDisplayName] = useState("");
  const [kind, setKind] = useState("normal");
  const [model, setModel] = useState("periodic_fill");
  const [priority, setPriority] = useState("1");
  const [disabled, setDisabled] = useState(false);
  const [mergedInto, setMergedInto] = useState("");

  useEffect(() => {
    if (!target) return;
    setDisplayName(target.display_name || formatTargetName(target));
    setKind(target.kind || "normal");
    setModel(target.model || "periodic_fill");
    setPriority(String(target.priority ?? 1));
    setDisabled(target.disabled);
    setMergedInto(target.merged_into_target_key || "");
  }, [target]);

  const mut = useMutation({
    mutationFn: () => {
      if (!target) throw new Error("target required");
      return updateMapRespawnTarget(gameID, {
        server_id: serverID,
        canonical_server_id: target.canonical_server_id,
        map_id: target.map_id,
        target_key: target.target_key,
        display_name: displayName.trim(),
        kind,
        model,
        disabled,
        priority: Number(priority) || 0,
        merged_into_target_key: mergedInto.trim(),
      });
    },
    onSuccess: () => {
      toast.success("刷怪目标已更新");
      onOpenChange(false);
      void queryClient.invalidateQueries({ queryKey: ["map-respawn-stats", gameID] });
    },
    onError: (e) => toast.error(`更新失败：${(e as Error).message}`),
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    mut.mutate();
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>校准刷怪目标</DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={submit}>
          <Field label="目标名">
            <Input value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
          </Field>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="类型">
              <Select value={kind} onValueChange={setKind}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">普通</SelectItem>
                  <SelectItem value="elite">精英</SelectItem>
                  <SelectItem value="boss">Boss</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="模型">
              <Select value={model} onValueChange={setModel}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="periodic_fill">周期补刷</SelectItem>
                  <SelectItem value="death_cooldown">死亡冷却</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="优先级">
              <Input value={priority} onChange={(e) => setPriority(e.target.value)} inputMode="decimal" />
            </Field>
            <Field label="合并 / 拆分">
              <Input value={mergedInto} onChange={(e) => setMergedInto(e.target.value)} placeholder="留空表示独立目标；填 target_key 表示合并" />
            </Field>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <Checkbox checked={disabled} onCheckedChange={(value) => setDisabled(value === true)} />
            禁用误识别目标
          </label>
          {target ? (
            <div className="rounded border border-[hsl(var(--border))] p-3 text-xs text-[hsl(var(--muted-foreground))]">
              <div className="font-mono">{target.target_key}</div>
              <div>{formatEvidence(target)}</div>
            </div>
          ) : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              取消
            </Button>
            <Button type="submit" disabled={mut.isPending}>
              保存
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function AutoMapHuntLeasesPanel({
  gameID,
  items,
  loading,
  error,
  mapNameByID,
  onRefresh,
}: {
  gameID: string;
  items: AutoMapHuntLeaseEntry[];
  loading: boolean;
  error: unknown;
  mapNameByID: Map<string, string>;
  onRefresh: () => void;
}) {
  const queryClient = useQueryClient();
  const releaseMut = useMutation({
    mutationFn: (input: { instance_id: string; conflict_key: string }) =>
      releaseAutoMapHuntLease(gameID, input),
    onSuccess: () => {
      toast.success("已释放占用");
      void queryClient.invalidateQueries({
        queryKey: ["auto-map-hunt-leases", gameID],
      });
    },
    onError: (e) => toast.error(`释放失败：${(e as Error).message}`),
  });

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between gap-3">
          <CardTitle>自动下图占用</CardTitle>
          <Button
            type="button"
            size="sm"
            variant="outline"
            className="gap-1"
            disabled={loading}
            onClick={onRefresh}
          >
            <RefreshCw className="size-3.5" aria-hidden="true" />
            刷新
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {error ? (
          <p className="text-xs text-[hsl(var(--danger))]">{(error as Error).message}</p>
        ) : items.length === 0 ? (
          <p className="text-xs text-[hsl(var(--muted-foreground))]">
            当前没有自动下图 lease。
          </p>
        ) : (
          <AutoMapHuntLeaseTable
            items={items}
            releasing={releaseMut.isPending}
            mapNameByID={mapNameByID}
            onRelease={(item) =>
              releaseMut.mutate({
                instance_id: item.instance_id,
                conflict_key: item.conflict_key,
              })
            }
          />
        )}
      </CardContent>
    </Card>
  );
}

function RespawnStatsTable({
  items,
  mapNameByID,
}: {
  items: MapRespawnStatEntry[];
  mapNameByID: Map<string, string>;
}) {
  return (
    <Table dense>
      <TableHeader>
        <TableRow>
          <TableHead>地图</TableHead>
          <TableHead>目标</TableHead>
          <TableHead>平均刷怪</TableHead>
          <TableHead>常见间隔</TableHead>
          <TableHead>高峰分钟</TableHead>
          <TableHead>样本数</TableHead>
          <TableHead>最后样本</TableHead>
          <TableHead>状态</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => (
          <TableRow key={`${item.canonical_server_id}:${item.map_id}:${item.target_key ?? ""}`}>
            <TableCell>
              <MapNameCell mapID={item.map_id} mapNameByID={mapNameByID} />
            </TableCell>
            <TableCell>{formatTargetScope(item)}</TableCell>
            <TableCell>{formatDurationMS(item.respawn_avg_ms)}</TableCell>
            <TableCell>{formatTopInterval(item)}</TableCell>
            <TableCell>{formatTopMinute(item)}</TableCell>
            <TableCell>{item.sample_count}</TableCell>
            <TableCell>{formatTime(item.last_sample_at)}</TableCell>
            <TableCell>
              <StatusBadge variant={item.sample_count >= 3 ? "ok" : "warn"}>
                {item.sample_count >= 3 ? "稳定" : "样本少"}
              </StatusBadge>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function AutoMapHuntLeaseTable({
  items,
  releasing,
  mapNameByID,
  onRelease,
}: {
  items: AutoMapHuntLeaseEntry[];
  releasing: boolean;
  mapNameByID: Map<string, string>;
  onRelease: (item: AutoMapHuntLeaseEntry) => void;
}) {
  return (
    <Table dense>
      <TableHeader>
        <TableRow>
          <TableHead>归一区服</TableHead>
          <TableHead>conflict key</TableHead>
          <TableHead>当前地图</TableHead>
          <TableHead>持有角色</TableHead>
          <TableHead>过期时间</TableHead>
          <TableHead>操作</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {items.map((item) => (
          <TableRow key={`${item.canonical_server_id}:${item.conflict_key}`}>
            <TableCell className="font-mono">{item.canonical_server_id}</TableCell>
            <TableCell className="font-mono">{item.conflict_key}</TableCell>
            <TableCell>
              <MapNameCell mapID={item.map_id} mapNameByID={mapNameByID} />
            </TableCell>
            <TableCell>
              {item.role_name || (
                <span className="font-mono text-xs">{item.instance_id}</span>
              )}
            </TableCell>
            <TableCell>{formatTime(item.expires_at)}</TableCell>
            <TableCell>
              <Button
                type="button"
                size="sm"
                variant="destructive"
                className="gap-1"
                disabled={releasing}
                onClick={() => onRelease(item)}
              >
                <X className="size-3.5" aria-hidden="true" />
                释放
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function MapNameCell({
  mapID,
  mapNameByID,
  compact = false,
}: {
  mapID: string;
  mapNameByID: Map<string, string>;
  compact?: boolean;
}) {
  const name = mapNameByID.get(mapID);
  if (!name || name === mapID) {
    return <span className="font-mono text-xs">{mapID}</span>;
  }
  if (compact) {
    return <span className="font-medium">{name}</span>;
  }
  return (
    <div>
      <div className="font-medium">{name}</div>
      <div className="font-mono text-xs text-[hsl(var(--muted-foreground))]">
        {mapID}
      </div>
    </div>
  );
}

function runtimeError(...errors: unknown[]) {
  for (const error of errors) {
    if (error instanceof Error) return error;
  }
  return null;
}

function formatDurationMS(ms: number) {
  if (!Number.isFinite(ms) || ms <= 0) return "-";
  const sec = ms / 1000;
  if (sec >= 60) {
    const min = sec / 60;
    return `${min.toFixed(min >= 10 ? 0 : 1)}m`;
  }
  return `${sec.toFixed(sec >= 10 ? 0 : 1)}s`;
}

function formatRatio(value: number) {
  if (!Number.isFinite(value) || value <= 0) return "";
  return `${Math.round(value * 100)}%`;
}

function formatTopInterval(item: MapRespawnStatEntry) {
  if (!item.top_interval_ms || !item.top_interval_count) return "-";
  const ratio = formatRatio(item.top_interval_ratio);
  return `${formatDurationMS(item.top_interval_ms)} · ${item.top_interval_count}次${ratio ? `/${ratio}` : ""}`;
}

function formatTopMinute(item: MapRespawnStatEntry) {
  if (item.top_minute_count <= 0) return "-";
  const minute = String(item.top_minute_of_hour).padStart(2, "0");
  const ratio = formatRatio(item.top_minute_ratio);
  return `每小时 ${minute} 分 · ${item.top_minute_count}次${ratio ? `/${ratio}` : ""}`;
}

function formatTargetScope(item: MapRespawnStatEntry) {
  if (!item.target_key) return "整图";
  if (item.monster_name) return item.monster_name;
  if (item.spawn_profile) return item.spawn_profile;
  if (item.target_kind) return item.target_kind;
  return item.target_key;
}

function formatTargetName(target: MapRespawnTargetEntry) {
  if (target.display_name) return target.display_name;
  if (target.aliases?.[0]) return target.aliases[0];
  if (target.target_key.includes(":")) return target.target_key.split(":").slice(-1)[0];
  return target.target_key;
}

function formatTargetKind(kind: string) {
  if (kind === "boss") return "Boss";
  if (kind === "elite") return "精英";
  return "普通";
}

function formatWindows(windows: MapRespawnWindowEntry[]) {
  if (!windows.length) return "-";
  return (
    <div className="space-y-1">
      {windows.slice(0, 3).map((window, index) => (
        <div key={`${window.kind}:${window.interval_ms}:${window.minute_of_hour}:${window.minute_of_day}:${index}`}>
          {formatWindow(window)}
        </div>
      ))}
    </div>
  );
}

function formatWindow(window: MapRespawnWindowEntry) {
  const confidence = formatRatio(window.confidence);
  if (window.kind === "boss_timer") {
    const ready = window.ready_at ? formatTime(window.ready_at) : "-";
    return `Boss 倒计时 ${ready}${confidence ? ` · ${confidence}` : ""}`;
  }
  if (window.kind === "clock_minute") {
    const minute = String(window.minute_of_hour).padStart(2, "0");
    return `每小时 ${minute} 分 · ${window.sample_count}次${confidence ? `/${confidence}` : ""}`;
  }
  if (window.kind === "clock_day") {
    const hour = String(Math.floor(window.minute_of_day / 60)).padStart(2, "0");
    const minute = String(window.minute_of_day % 60).padStart(2, "0");
    return `每天 ${hour}:${minute} · ${window.sample_count}次${confidence ? `/${confidence}` : ""}`;
  }
  const label = window.kind === "death_cooldown" ? "死亡冷却" : "间隔";
  return `${label} ${formatDurationMS(window.interval_ms)} · ${window.sample_count}次${confidence ? `/${confidence}` : ""}`;
}

function formatEvidence(target: MapRespawnTargetEntry) {
  const parts: string[] = [];
  if (target.aliases?.length) parts.push(`alias ${target.aliases.slice(0, 3).join("/")}`);
  if (target.type_indexes?.length) parts.push(`type ${target.type_indexes.slice(0, 3).join("/")}`);
  if (target.regions?.length) parts.push(`${target.regions.length} 区域`);
  return parts.join(" · ") || target.source || "-";
}

function formatTime(ts: number) {
  if (!ts) return "-";
  return new Date(ts).toLocaleString();
}
